import React, { Fragment } from "react";
import { Form, Col, Row, Card } from "react-bootstrap";

import {
  BANK_LOCATION_OTHER_OPTION,
  DECLARATION_PLACES,
  DEFAULT_BANKS,
  DEFAULT_BANK_IFSC_CODES,
  embeddedSpacesPattern,
  IFSCCodePattern,
  OTHER_OPTION,
  positiveNumberPattern
} from "../../../../../constants/constants";
import useValidation from "../../../../../util/useValidation";

const BankingInformation = ({
  otherBankDetails,
  personalDetails,
  updateOtherBankDetails,
  updatePersonalDetails
}) => {
  const { handleOnBlurValidation, errors } = useValidation();
  const {
    bankAccountNumber,
    bankBranch,
    bankLocation,
    bankName,
    bankIFSCCode,
    confirmBankAccountNumber
  } = personalDetails;

  const { otherBankName, otherBankLocation, otherIFSCCode } = otherBankDetails;

  const handleOnChangePersonalDetails = ({ target: { name, value } }) => {
    if (embeddedSpacesPattern.test(value)) {
      if (name === "bankAccountNumber") {
        updatePersonalDetails("confirmBankAccountNumber", "");
      }
      updatePersonalDetails(name, value.trimStart());
    }
  };

  const handleOnChangeOtherBankDetails = ({ target: { name, value } }) => {
    if (embeddedSpacesPattern.test(value)) {
      updateOtherBankDetails(name, value.trimStart());
    }
  };

  const handleOnBlurInput = e => {
    handleOnBlurValidation(e);
  };

  const emptyBankDetailsExceptBankName = () => {
    updatePersonalDetails("bankAccountNumber", "");
    updatePersonalDetails("confirmBankAccountNumber", "");
    updatePersonalDetails("bankBranch", "");
    updatePersonalDetails("bankLocation", "");
    updateOtherBankDetails("otherIFSCCode", "");
    updateOtherBankDetails("otherBankLocation", "");
  };

  const handleBankChange = ({ target: { name, value } }) => {
    updatePersonalDetails(name, value);
    updatePersonalDetails("bankIFSCCode", value);
    updateOtherBankDetails("otherBankName", "");
    emptyBankDetailsExceptBankName();
  };

  const handleIFSCChange = ({ target: { name, value } }) => {
    updatePersonalDetails(name, value);
    emptyBankDetailsExceptBankName();
  };

  const handleLocationChange = ({ target: { name, value } }) => {
    updatePersonalDetails(name, value);
    updateOtherBankDetails("otherBankLocation", "");
  };

  const areAccountFieldsMandatory =
    bankName == OTHER_OPTION.id || bankIFSCCode == OTHER_OPTION.id;

  return (
    <Card className="mt-2">
      <Card.Header className="text-center">Banking Information</Card.Header>
      <Card.Body>
        <Form.Group as={Row} controlId="bankNameAndIFSCCode">
          <Form.Label column sm={2}>
            Bank Name*
          </Form.Label>
          <Col sm={3}>
            <Form.Control
              size="sm"
              as="select"
              name="bankName"
              required
              value={bankName}
              onChange={handleBankChange}
              onBlur={handleOnBlurInput}
              isInvalid={errors.bankName}
            >
              <option value="">Choose...</option>
              {DEFAULT_BANKS.map(bank => (
                <option value={bank.id}>{bank.name}</option>
              ))}
              <option value={OTHER_OPTION.id}>{OTHER_OPTION.text}</option>
            </Form.Control>
            {errors.bankName && (
              <Form.Control.Feedback type="invalid">
                {errors.bankName.type === "required" &&
                  "Bank Account is required"}
              </Form.Control.Feedback>
            )}
          </Col>
          <Form.Label column sm={2}>
            IFSC Code*
          </Form.Label>
          <Col sm={3}>
            <Form.Control
              size="sm"
              as="select"
              name="bankIFSCCode"
              required
              value={bankIFSCCode}
              onChange={handleIFSCChange}
              onBlur={handleOnBlurInput}
              isInvalid={!bankIFSCCode && errors.bankIFSCCode}
            >
              <option value="">Choose...</option>
              {DEFAULT_BANK_IFSC_CODES.map(ifsc => (
                <option value={ifsc.id}>{ifsc.code}</option>
              ))}
              <option value={OTHER_OPTION.id}>{OTHER_OPTION.text}</option>
            </Form.Control>
            {errors.bankIFSCCode && (
              <Form.Control.Feedback type="invalid">
                {errors.bankIFSCCode.type === "required" &&
                  "IFSC Code is required"}
              </Form.Control.Feedback>
            )}
          </Col>
        </Form.Group>
        {areAccountFieldsMandatory && (
          <Form.Group as={Row} controlId="otherBankAndOtherIFSC">
            {bankName == OTHER_OPTION.id && (
              <Fragment>
                <Form.Label column sm={2}>
                  Other Bank*
                </Form.Label>
                <Col sm={3}>
                  <Form.Control
                    size="sm"
                    required
                    placeholder="Other Bank Name"
                    type="text"
                    name="otherBankName"
                    value={otherBankName}
                    onChange={handleOnChangeOtherBankDetails}
                    onBlur={handleOnBlurInput}
                    isInvalid={errors.otherBankName}
                  />
                  {errors.otherBankName && (
                    <Form.Control.Feedback type="invalid">
                      {errors.otherBankName.type === "required" &&
                        "Other Bank is required"}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Fragment>
            )}
            {bankIFSCCode == OTHER_OPTION.id && (
              <Fragment>
                <Form.Label column sm={2}>
                  Other IFSC Code*
                </Form.Label>
                <Col sm={3}>
                  <Form.Control
                    size="sm"
                    required
                    maxLength={11}
                    pattern={IFSCCodePattern}
                    placeholder="Other IFSC Code"
                    type="text"
                    name="otherIFSCCode"
                    value={otherIFSCCode}
                    onChange={handleOnChangeOtherBankDetails}
                    onBlur={handleOnBlurInput}
                    isInvalid={errors.otherIFSCCode}
                  />
                  {errors.otherIFSCCode && (
                    <Form.Control.Feedback type="invalid">
                      {errors.otherIFSCCode.type === "required" &&
                        "Other IFSC Code is required"}
                      {errors.otherIFSCCode.type === "pattern" &&
                        "Please enter valid IFSC code"}
                    </Form.Control.Feedback>
                  )}
                </Col>
              </Fragment>
            )}
          </Form.Group>
        )}
        <Form.Group
          as={Row}
          controlId="bankAccountNumberAndConfirmBankAccountNumber"
        >
          <Form.Label column sm={2}>
            Account No.{areAccountFieldsMandatory && "*"}
          </Form.Label>
          <Col sm={3}>
            <Form.Control
              className="password-type"
              size="sm"
              required={areAccountFieldsMandatory}
              placeholder="Bank Account No."
              type="text"
              name="bankAccountNumber"
              maxLength={16}
              pattern={positiveNumberPattern}
              value={bankAccountNumber}
              onChange={handleOnChangePersonalDetails}
              onBlur={handleOnBlurInput}
              isInvalid={errors.bankAccountNumber}
            />
            {errors.bankAccountNumber && (
              <Form.Control.Feedback type="invalid">
                {errors.bankAccountNumber.type === "required" &&
                  "Bank Account Number is required"}
                {errors.bankAccountNumber.type === "pattern" &&
                  "Bank Account Number should be a numeric value"}
              </Form.Control.Feedback>
            )}
          </Col>
          <Form.Label column sm={2} className="pr-1">
            Confirm Account No.
            {areAccountFieldsMandatory || bankAccountNumber.length ? "*" : ""}
          </Form.Label>
          <Col sm={3}>
            <Form.Control
              size="sm"
              required={areAccountFieldsMandatory || bankAccountNumber.length}
              placeholder="Bank Account No."
              type="text"
              name="confirmBankAccountNumber"
              maxLength={16}
              pattern={bankAccountNumber}
              value={confirmBankAccountNumber}
              onChange={handleOnChangePersonalDetails}
              onBlur={handleOnBlurInput}
              isInvalid={errors.confirmBankAccountNumber}
            />
            {errors.confirmBankAccountNumber && (
              <Form.Control.Feedback type="invalid">
                {errors.confirmBankAccountNumber.type === "required" &&
                  "Confirm Bank Account Number is required"}
                {errors.confirmBankAccountNumber.type === "pattern" &&
                  "Account Number mismatch with the added"}
              </Form.Control.Feedback>
            )}
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="LocationAndBranch">
          <Form.Label column sm={2}>
            Location{areAccountFieldsMandatory && "*"}
          </Form.Label>
          <Col sm={3}>
            <Form.Control
              size="sm"
              as="select"
              name="bankLocation"
              required={areAccountFieldsMandatory}
              value={bankLocation}
              onChange={handleLocationChange}
              onBlur={handleOnBlurInput}
              isInvalid={areAccountFieldsMandatory && errors.bankLocation}
            >
              <option value="">Choose...</option>
              {DECLARATION_PLACES.map(place => (
                <option value={place.id}>{place.name}</option>
              ))}
              <option value={BANK_LOCATION_OTHER_OPTION.id}>
                {BANK_LOCATION_OTHER_OPTION.text}
              </option>
            </Form.Control>
            {areAccountFieldsMandatory && errors.bankLocation && (
              <Form.Control.Feedback type="invalid">
                {errors.bankLocation.type === "required" &&
                  "Bank Location is required"}
              </Form.Control.Feedback>
            )}
          </Col>
          <Form.Label column sm={2}>
            Branch{areAccountFieldsMandatory && "*"}
          </Form.Label>
          <Col sm={3}>
            <Form.Control
              size="sm"
              required={areAccountFieldsMandatory}
              placeholder="Branch"
              type="text"
              name="bankBranch"
              value={bankBranch}
              onChange={handleOnChangePersonalDetails}
              onBlur={handleOnBlurInput}
              isInvalid={errors.bankBranch}
            />
            {errors.bankBranch && (
              <Form.Control.Feedback type="invalid">
                {errors.bankBranch.type === "required" &&
                  "Bank Branch is required"}
              </Form.Control.Feedback>
            )}
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="OtherLocation">
          {bankLocation == BANK_LOCATION_OTHER_OPTION.id && (
            <Fragment>
              <Form.Label column sm={2}>
                Other Location{areAccountFieldsMandatory && "*"}
              </Form.Label>
              <Col sm={3}>
                <Form.Control
                  size="sm"
                  required={areAccountFieldsMandatory}
                  placeholder="Other Bank Location"
                  type="text"
                  name="otherBankLocation"
                  value={otherBankLocation}
                  onChange={handleOnChangeOtherBankDetails}
                  onBlur={handleOnBlurInput}
                  isInvalid={
                    areAccountFieldsMandatory && errors.otherBankLocation
                  }
                />
                {areAccountFieldsMandatory && errors.otherBankLocation && (
                  <Form.Control.Feedback type="invalid">
                    {errors.otherBankLocation.type === "required" &&
                      "Other Bank Location is required"}
                  </Form.Control.Feedback>
                )}
              </Col>
            </Fragment>
          )}
        </Form.Group>
      </Card.Body>
    </Card>
  );
};

export default BankingInformation;
